import styled from "styled-components";
import { BreadcrumbsCommon, ClipLoaderCommon, StyledButton } from "@components";
import { AppModuleLayout } from "@layouts";
import {
    PATH_HOME,
    PATH_LINK_AGENT_ACCOUNT,
    PATH_MY_ACCOUNT,
    PATH_PRIVACY_POLICY,
    PATH_TERM,
} from "@routes";
import { breakpoint, theme } from "@utilities";
import { useEffect, useState } from "react";
import { pointsApi } from "@api";
import { initializeAccountDetail, selectAccount, useAppDispatch, useAppSelector } from "@redux";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { useNavigate, useLocation } from "react-router-dom";
import { IPointsData } from "@interfaces";

export const LinkAgentAccount = () => {
    const [showUpgradePopup, setShowUpgradePopup] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const [isUpgrading, setIsUpgrading] = useState(false);
    const { account } = useAppSelector(selectAccount);
    const [pointsData, setPointsData] = useState<IPointsData>({
        subSystemType: "",
        pointsBalance: 0,
        availableBalance: 0,
        pointsMultiplier: 0,
    });

    const isAccountIncomplete = !(
        account?.title &&
        account?.first_name &&
        account?.last_name &&
        account?.email &&
        account?.phone_number &&
        account?.post_code &&
        account?.country_code
    );

    const breadcrumbsData = [
        { title: "Home", link: PATH_HOME },
        { title: "Upgrade", link: PATH_LINK_AGENT_ACCOUNT },
    ];

    const BENEFITS = [
        "Earn points every time you book -- for yourself or someone else",
        "Track your rewards in real-time",
        "Boost your balance by booking with friends",
        "Earn referral points when other book through you",
        "Pay with points for your own travel or gift them to others",
        "Share trips with friends & family for even more adventures",
        "More bookings = More rewards.",
    ];

    const handleActualUpgrade = async (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ): Promise<void> => {
        event.preventDefault();
        if (isAccountIncomplete) {
            if (location.pathname !== PATH_MY_ACCOUNT) {
                const returnPath = encodeURIComponent(location.pathname + location.search);
                ErrorToast({ Message: "Please complete your account details first" });
                navigate(`${PATH_MY_ACCOUNT}?returnPath=${returnPath}`);
            }
            return; // Stop here, don't proceed with the upgrade
        }

        setIsUpgrading(true);
        setShowUpgradePopup(false);

        try {
            // Get referral from URL params if available
            const urlParams = new URLSearchParams(location.search);
            const referral = urlParams.get("referrer");

            const res = await pointsApi.upgradeToPointsAgent(referral || undefined);
            if (res.data.success) {
                await dispatch(initializeAccountDetail());

                // Redirect back to checkout step 2 if coming from there
                const fromLocation = (location.state as any)?.from;
                if (fromLocation?.pathname?.includes("/quotes/")) {
                    navigate(fromLocation.pathname + fromLocation.search);
                    return;
                }
            }
        } catch (error: any) {
            if (error?.response?.data?.title) {
                ErrorToast({
                    Message: error.response.data.title,
                });
            }
        } finally {
            setIsUpgrading(false);
        }
    };

    useEffect(() => {
        if (showUpgradePopup) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [showUpgradePopup]);

    useEffect(() => {
        if (!account?.is_agent_integrated) return;
        pointsApi.getPointsData().then((res) => {
            setPointsData(res.data.data);
        });
    }, [account?.is_agent_integrated]);

    return (
        <AppModuleLayout>
            <StyledUpgradeAgentPage>
                {showUpgradePopup && (
                    <PopupOverlay onClick={() => setShowUpgradePopup(false)}>
                        <PopupContent onClick={(e) => e.stopPropagation()}>
                            <h3>Upgrade Account?</h3>
                            <p>Are you sure you want to upgrade your account to points agent?</p>
                            <PopupActions>
                                <PopupButton $primary onClick={handleActualUpgrade}>
                                    Yes, Upgrade
                                </PopupButton>
                                <PopupButton onClick={() => setShowUpgradePopup(false)}>
                                    Cancel
                                </PopupButton>
                            </PopupActions>
                        </PopupContent>
                    </PopupOverlay>
                )}
                <div className="wrapper">
                    <div className="breadCrumbs">
                        <BreadcrumbsCommon data={breadcrumbsData} />
                    </div>
                    <p className="heading">Upgrade Your Account</p>
                    <div className="subWrapper">
                        <ContainerBody>
                            {account?.is_agent_integrated ? (
                                <>
                                    <div className="agentAccount">
                                        <StyledSuccessContainer>
                                            <img
                                                src="/images/LargeTickMark.svg"
                                                alt="LargeTickMark"
                                            />
                                            <StyledSuccessHeading>
                                                Upgraded to Points Agent Successfully.
                                            </StyledSuccessHeading>
                                            <StyledSuccessMessage>
                                                You’ve been successfully upgraded to a Points Agent!
                                                Now you can start exploring and earning points—book
                                                trips to collect points, invite friends to earn
                                                referral bonuses, check your points anytime, and
                                                redeem them your way.
                                            </StyledSuccessMessage>
                                            <StyledCenteredDiv>
                                                <h6>Agent Name</h6>
                                                <p>
                                                    {account.first_name} {account.last_name}
                                                </p>
                                            </StyledCenteredDiv>
                                            {pointsData.availableBalance > 0 && (
                                                <StyledCenteredDiv>
                                                    <h6>Points Balance</h6>
                                                    <h2>{pointsData.availableBalance} Points</h2>
                                                </StyledCenteredDiv>
                                            )}
                                        </StyledSuccessContainer>
                                    </div>
                                    <StyledFooter>
                                        <StyledButtonUpgrade
                                            $borderRadius={theme.borderRadius.small}
                                            color={theme.gradients.button}
                                            onClick={() => navigate(PATH_HOME)}
                                        >
                                            Back to Homepage
                                        </StyledButtonUpgrade>
                                    </StyledFooter>
                                </>
                            ) : (
                                <>
                                    <div className="agentAccount">
                                        <AgentInfoContainer>
                                            <AgentInfo>
                                                <h3>Upgrade Your Account & Start Earning!</h3>
                                                <p>
                                                    Upgrade today and start turning your travel into
                                                    unforgettable experiences!
                                                </p>
                                                <h4>Here's how it works:</h4>
                                                <Divider />
                                                {BENEFITS.map((benefit, index) => (
                                                    <InfoContainer key={index}>
                                                        <img
                                                            src="/images/TickMark.svg"
                                                            alt="check"
                                                        />
                                                        <Info>{benefit}</Info>
                                                    </InfoContainer>
                                                ))}
                                            </AgentInfo>
                                        </AgentInfoContainer>
                                    </div>
                                    <Footer>
                                        {isUpgrading ? (
                                            <StyledButtonUpgrade
                                                $borderRadius={theme.borderRadius.small}
                                                disabled
                                            >
                                                <ClipLoaderCommon
                                                    size={24}
                                                    color={theme.colors.highlight}
                                                    margin={10}
                                                />
                                            </StyledButtonUpgrade>
                                        ) : (
                                            <StyledButtonUpgrade
                                                $borderRadius={theme.borderRadius.small}
                                                color={theme.gradients.button}
                                                onClick={() => setShowUpgradePopup(true)}
                                            >
                                                Upgrade to Points Agent
                                            </StyledButtonUpgrade>
                                        )}

                                        <TermsAndCondition>
                                            By continuing, you agree to our{" "}
                                            <a href={PATH_TERM}>Terms of Service</a> and{" "}
                                            <a href={PATH_PRIVACY_POLICY}>Privacy Policy</a>
                                        </TermsAndCondition>
                                    </Footer>
                                </>
                            )}
                        </ContainerBody>
                    </div>
                </div>
            </StyledUpgradeAgentPage>
        </AppModuleLayout>
    );
};

const StyledFooter = styled.div`
    position: fixed;
    bottom: 0;
    width: 100%;
    padding: 16px;
    display: flex;
    justify-content: center;
    align-items: center;

    ${breakpoint.breakMedium`
        position: static;
        padding: 24px;
    `}

    ${breakpoint.breakMedium`
        position: static;
        padding: 24px;
        margin-bottom : 104px;
    `}
`;

const StyledSuccessContainer = styled.div`
    font-family: Poppins;
    line-height: 120%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 104px 24px 0px 24px;
    gap: 24px;

    ${breakpoint.breakOnlyMobile`
        background-color: #fff;
        flex: 1;
        border-radius: 8px;
        min-height: calc(88vh - 200px);
        justify-content: center;
        align-items: center;;
        padding: 24px 24px;
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
    `}
`;

const StyledSuccessHeading = styled.h2`
    font-family: "Plus Jakarta Sans";
    text-align: center;
    font-weight: 700;
    font-size: 18px;
`;

const StyledSuccessMessage = styled.p`
    text-align: center;
    color: #6e7375;
    line-height: 120%;
`;

const StyledCenteredDiv = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    text-align: center;
    h6 {
        font-weight: 400;
    }
    h2 {
        font-weight: 600;
        color: ${theme.colors.highlight};
        font-size: 24px;
    }
    p {
        font-weight: 500;
        color: ${theme.colors.highlight};
        font-size: 18px;
    }
`;

const StyledUpgradeAgentPage = styled.div`
    padding: 16px;
    background-color: #f2f2f2;
    flex: 1;

    .agentAccount {
        flex: 1;
        ${breakpoint.breakTablet`
            margin-top: 0;
        `}
    }

    .wrapper {
        margin: 0 auto;
        .heading {
            font-size: 18px;
            font-family:
                Plus Jakarta Sans,
                sans-serif;
            font-weight: 700;
            line-height: 120%;
            margin-top: 16px;
            margin-bottom: 15px;
            display: flex;
            gap: 30px;

            ${breakpoint.breakIpadPro`
                font-size: 28px;
                margin-bottom: 30px;
                margin-top: 24px;
            `}
        }

        ${breakpoint.breakTablet`
            width: ${(p) => p.theme.widths.wide_700};
        `}

        ${breakpoint.breakIpadPro`
            width: ${(p) => p.theme.widths.wide_900};
        `}

        ${breakpoint.breakLarge`
            width: ${(p) => p.theme.widths.wide_1110};
        `}
    }

    .menu {
        display: none;

        ${breakpoint.breakTablet`
            display: block;
        `}
    }

    .breadCrumbs {
        ${breakpoint.breakTablet`
            margin-top: 14px;
        `}
        ${breakpoint.breakOnlyMobile`
            left: 16px;
        `}
    }

    .subWrapper {
        ${breakpoint.breakTablet`
            display: flex;
            flex-direction: column;
            margin-top: 30px;
            justify-content: center;
            align-items: center;
        `}
    }
`;

const ContainerBody = styled.div`
    background-color: #fff;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
    margin-bottom: 112px;
    width: 736px;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);

    ${breakpoint.breakOnlyMobile`
        background-color: transparent;
        display: flex;
        flex-direction: column;
        margin-bottom: 0px;
        justify-content: start;
        width: 100%;
        box-shadow: none;
    `}
`;

const AgentInfoContainer = styled.div`
    font-family: Poppins, sans-serif;
    line-height: 120%;
    display: flex;
    justify-content: center;
    align-items: center;

    ${breakpoint.breakOnlyMobile`
        flex: 1;
        border-radius: 8px;
        min-height: calc(88vh - 200px);
        justify-content: start;
        align-items: start;
    `}
`;

const AgentInfo = styled.div`
    background-color: #fff;
    padding: 32px 0px 0px 0px;
    border-radius: 8px;
    width: 380px;

    ${breakpoint.breakOnlyMobile`
        padding: 16px;
        width: 100%;
    `}

    h3 {
        font-weight: 600;
        font-size: 18px;
        color: ${theme.colors.red};
    }

    p {
        font-size: 14px;
        color: #6e7375;
        margin-top: 8px;
        line-height: 150%;
    }

    h4 {
        font-size: 16px;
        font-weight: 600;
        color: #131214;
        margin-top: 12px;
    }
`;

const Divider = styled.div`
    margin: 16px 0 16px 0;
    border-top: 1px solid;
    border-color: #e6e9eb;
    ${breakpoint.breakOnlyMobile`
        margin: 12px 0 12px 0;
    `}
`;

const InfoContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 12px;
`;

const Info = styled.p`
    font-size: 14px;
    margin: 0px !important;
    color: #131214 !important;
`;

const Footer = styled.div`
    display: flex;
    gap: 24px;
    flex: 1;
    font-family: Poppins, sans-serif;
    flex-direction: column;
    align-items: center;
    padding: 20px 0;

    ${breakpoint.breakOnlyMobile`
        position: sticky;
        bottom: 0;
        background: #f2f2f2;
    `}
`;

const StyledButtonUpgrade = styled(StyledButton)`
    margin-top: 0px;
    width: 100%;
    max-width: 360px;
    font-weight: 500;
    ${breakpoint.breakOnlyMobile`
            max-width: none;
    `}
`;

const TermsAndCondition = styled.p`
    font-size: 12px;
    color: #6e7375;
    text-align: center;
    width: 360px;
    line-height: 150%;
    /* The links sit in a grey sentence; grey keeps them part of it, and the
       underline is what marks them as links. */
    a {
        color: #747474;
        text-decoration: underline;
    }
    ${breakpoint.breakOnlyMobile`
        width:100%
    `}
`;

const PopupOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
`;

const PopupContent = styled.div`
    background: white;
    padding: 2rem;
    border-radius: 8px;
    max-width: 400px;
    text-align: center;

    ${breakpoint.breakOnlyMobile`
        max-width: 350px;
    `}

    h3 {
        margin-bottom: 1rem;
        font-size: 24px;
    }

    p {
        margin-bottom: 2rem;
        color: #6e7375;
    }
`;

const PopupActions = styled.div`
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-direction: column;
`;

const PopupButton = styled.button<{ $primary?: boolean }>`
    padding: 8px 24px;
    border-radius: 4px;
    height: 48px;
    border: ${({ $primary }) => ($primary ? "none" : `2px solid ${theme.colors.main}`)};
    background: ${({ $primary }) =>
        $primary
            ? `linear-gradient(90deg, ${theme.gradients.button[0]}, ${theme.gradients.button[1]})`
            : "#fff"};
    color: ${({ $primary }) => ($primary ? theme.colors.button_text : theme.colors.button_text1)};
    cursor: pointer;
    font-size: 16px;
    font-weight: 500;
`;
